import type { Ajv } from "ajv";
import addFormats from "ajv-formats";
import Ajv2020 from "ajv/dist/2020";
import { uniq } from "lodash-es";

import { PropertyValue, SubSchema } from "src/api/flowTypes";
import { isRealUnion, JsonSchema, JsonSchemaNode } from "src/jsonSchema/types";
import { JsonRefPathPrefix } from "src/schema/jsonRefUtils";

const internalSchemaDefKeys = [
  "FeaturesGlobal",
  "Features@",
  "Entities@",
  "case_properties@",
  "Events@",
  "Address",
  "IpAddress",
  "LatLong",
  "Url",
  "File",
] as const;

export const isInternalSchemaDefKey = (key: string) => {
  if (key.startsWith(JsonRefPathPrefix)) {
    key = key.replace(JsonRefPathPrefix, "");
  }
  return internalSchemaDefKeys.some(
    (internalKey) =>
      key === internalKey || (key.includes("@") && key.startsWith(internalKey)),
  );
};

export type ValidationError = {
  type: "json" | "structure" | "refs" | "custom";
  message: string;
};

export type CustomValidator = (
  schema: JsonSchema,
  path?: string,
) => ValidationError | null;

export const createDefaultAjv = (allowUnionTypes: boolean = false): Ajv => {
  const ajv = new Ajv2020({
    allErrors: true,
    strict: true,
    strictTypes: true,
    allowUnionTypes,
  });
  addFormats(ajv);

  // Whitelist custom keywords
  ajv.addKeyword("sensitive");
  ajv.addKeyword("order");

  return ajv;
};

export const validateJsonSchema = (
  jsonString: string,
  customValidators: CustomValidator[] = [],
  ajvValidator: Ajv = createDefaultAjv(),
  internalSubschemas: Record<string, SubSchema<PropertyValue>> = {},
): ValidationError | null => {
  // Validate JSON object structure
  let parsed: JsonSchema;
  let parsedWithInternalSubschemas: JsonSchema;
  try {
    parsed = JSON.parse(jsonString) as JsonSchema;
    parsedWithInternalSubschemas = {
      ...parsed,
      $defs: {
        ...internalSubschemas,
        ...parsed.$defs,
      },
    };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    return { type: "json", message: `Invalid JSON: ${message}` };
  }

  // Run custom validators (if any)
  for (const validator of customValidators) {
    const error = validator(parsed);
    if (error) {
      return {
        type: error.type,
        message: `Validation error: ${error.message}`,
      };
    }
  }

  if (ajvValidator) {
    // Delete $schema to prevent Ajv from trying to resolve the $schema URI and fail
    const parsedSchema = parsedWithInternalSubschemas;
    const schemaUri = parsedSchema.$schema;
    if (schemaUri) {
      delete parsedSchema.$schema;
    }

    // Validate against JSON schema meta-schema
    try {
      const isValidSchema = ajvValidator.validateSchema(parsedSchema);
      if (!isValidSchema && ajvValidator.errors?.length) {
        const err = ajvValidator.errors[0];
        return {
          type: "structure",
          message: `Schema error at ${err.schemaPath}: ${err.message}`,
        };
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      return {
        type: "structure",
        message: `Schema error: ${message}`,
      };
    }

    // Compile to catch missing $refs
    try {
      ajvValidator.compile(parsedSchema);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      return {
        type: "refs",
        message: `Schema error: ${message}`,
      };
    }

    // Restore previously deleted schema URI (if any)
    if (schemaUri) {
      parsedSchema.$schema = schemaUri;
    }
  }

  return null; // valid
};

// - Custom validators

export const noRealUnions: CustomValidator = (
  schema: JsonSchema,
  path = "#",
) => {
  return traverseSchema(
    schema,
    (node, currentPath) => {
      if (isRealUnion(node)) {
        const hasAnyOf = "anyOf" in node && node.anyOf;
        const unionType = hasAnyOf ? "anyOf" : "oneOf";
        return {
          type: "custom",
          message: `Real union types are not allowed. Found ${unionType} with multiple non-null types at ${currentPath}`,
        };
      }
      return null;
    },
    path,
  );
};

export const noAllOf: CustomValidator = (schema: JsonSchema, path = "#") => {
  return traverseSchema(
    schema,
    (node, currentPath) => {
      if ("allOf" in node && node.allOf) {
        return {
          type: "custom",
          message: `allOf is not allowed at ${currentPath}`,
        };
      }
      return null;
    },
    path,
  );
};

export const noInternalSchemaDefs: CustomValidator = (
  schema: JsonSchema,
  path = "#",
) => {
  const defsPath = `${path}/$defs/`;

  return traverseSchema(
    schema,
    (_node, currentPath) => {
      if (currentPath.startsWith(defsPath)) {
        const normalizedPath = currentPath.replace(defsPath, "");
        if (isInternalSchemaDefKey(normalizedPath)) {
          return {
            type: "custom" as const,
            message: `${currentPath} is not allowed due to conflict with internal subschemas.`,
          };
        }
      }
      return null;
    },
    path,
  );
};

export const noNullOnlyTypes: CustomValidator = (
  schema: JsonSchema,
  path = "#",
) => {
  return traverseSchema(
    schema,
    (node, currentPath) => {
      const isUnion =
        currentPath.includes("anyOf") || currentPath.includes("oneOf");
      if (
        typeof node === "object" &&
        node !== null &&
        "type" in node &&
        !isUnion
      ) {
        const type = Array.isArray(node.type) ? uniq(node.type) : [node.type];
        if (type.length === 1 && type[0] === "null") {
          return {
            type: "custom" as const,
            message: `${currentPath} is not supported because it only allows null values.`,
          };
        }
      }

      const isUnionNode =
        node !== null &&
        typeof node === "object" &&
        ("oneOf" in node || "anyOf" in node);

      if (isUnionNode) {
        const members = node.anyOf ?? node.oneOf ?? [];
        const memberTypes = uniq(
          members
            .flatMap((member) => {
              if ("type" in member) {
                return member.type;
              }
              if ("$ref" in member) {
                return member["$ref"];
              }
              return null;
            })
            .filter(Boolean),
        );
        // checks if the union only contains null values
        // length 0 is already handled by the json schema validator
        if (memberTypes.length === 1 && memberTypes[0] === "null") {
          return {
            type: "custom" as const,
            message: `${currentPath} is not supported because it only allows null values.`,
          };
        }
      }
      return null;
    },
    path,
  );
};

// - Traversal helper

type VisitorFunc = (
  node: JsonSchemaNode,
  path: string,
) => ValidationError | null;

// Pre-order DFS helper for invoking a visitor function on every node of the schema tree
const traverseSchema = (
  schema: JsonSchema,
  visitorFunc: VisitorFunc,
  initialPath = "#",
): ValidationError | null => {
  const traverse = (
    obj: JsonSchemaNode,
    currentPath: string,
  ): ValidationError | null => {
    if (!obj || typeof obj !== "object") {
      return null;
    }

    const nodeError = visitorFunc(obj, currentPath);
    if (nodeError) {
      return nodeError;
    }

    for (const key in obj) {
      const value = (obj as Record<string, unknown>)[key];
      if (typeof value !== "object" || value === null) {
        continue; // skip non-object values
      }
      const newPath = `${currentPath}/${key}`;
      const error = traverse(value as JsonSchemaNode, newPath);
      if (error) {
        return error;
      }
    }

    return null;
  };

  return traverse(schema, initialPath);
};
